import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, CalendarDays, ListTodo } from 'lucide-react';
import { getPrimaryNavItems } from 'components/layout/navigationData';
import { useAuth } from 'contexts/AuthContext';
import { useTimezone } from 'contexts/TimezoneContext';
import { checklist, meetings } from 'services/api';
import type { ChecklistItem } from 'services/api/checklistClient';
import { hasChecklistLink, resolveChecklistLink } from 'utils/checklistLinks';
import { resolveMeetingTimeZone } from 'utils/meetingTime';
import { buildComingUpItems, calendarDateLabel, localCalendarDate } from './comingUpItems';
import './ComingUp.css';

const loadMeetings = async () => {
  const response = await meetings.getMeetings({ scope: 'upcoming' });
  return Array.isArray(response) ? response : response.results;
};
const loadTasks = () => checklist.list();

/** Each source can refresh or fail without clearing the other's successful data. */
const useComingUpSource = <T,>(load: () => Promise<T[]>, userKey: string) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  const alive = useRef(false);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError(false);
    try {
      const next = await load();
      if (alive.current && request === generation.current) setData(next);
    } catch {
      if (alive.current && request === generation.current) setError(true);
    } finally {
      if (alive.current && request === generation.current) setLoading(false);
    }
  }, [load]);
  useEffect(() => {
    alive.current = true;
    setData([]);
    void refresh();
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => {
      alive.current = false;
      generation.current += 1;
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh, userKey]);
  return { data, loading, error, refresh };
};

export const ComingUp = () => {
  const { user, isAuthLoading } = useAuth();
  const hasActiveCohort = getPrimaryNavItems(user ?? undefined).some((item) => item.path === '/mentorship');
  // Wait for membership to settle before mounting sources or exposing cohort content.
  if (isAuthLoading || !hasActiveCohort) return null;
  return <ComingUpContent key={user?.id} />;
};

const ComingUpContent = () => {
  const { user } = useAuth();
  const { timezone: savedTimezone } = useTimezone();
  const timezone = resolveMeetingTimeZone(undefined, savedTimezone);
  const navigate = useNavigate();
  const userKey = String(user?.id ?? '');
  const meetingSource = useComingUpSource(loadMeetings, userKey);
  const taskSource = useComingUpSource(loadTasks, userKey);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const today = localCalendarDate(new Date(), timezone);
  const items = buildComingUpItems(meetingSource.data, taskSource.data, timezone).slice(0, 3);
  const canViewCohort = getPrimaryNavItems(user ?? undefined).some((item) => item.path === '/mentorship');
  const openTask = async (task: ChecklistItem) => {
    setOpeningId(task.id);
    setLinkError(null);
    try {
      const link = await resolveChecklistLink(task);
      if (!mounted.current) return;
      if (!link) throw new Error('Resource unavailable');
      if (!link.newTab) navigate(link.url);
      else {
        const anchor = document.createElement('a');
        anchor.href = link.url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        if (link.download) anchor.download = task.title;
        anchor.click();
      }
    } catch {
      if (mounted.current) setLinkError(`Unable to open ${task.title}. Please try again.`);
    } finally {
      if (mounted.current) setOpeningId(null);
    }
  };

  return (
    <section className="coming-up" aria-labelledby="coming-up-heading" data-testid="home-coming-up">
      <div className="coming-up__header">
        <h2 id="coming-up-heading">Coming up</h2>
        {canViewCohort && <Link to="/mentorship">View cohort <ArrowUpRight size={14} aria-hidden="true" /></Link>}
      </div>
      <ul className="coming-up__list">
        {items.map((entry) => {
          const dateLabel = calendarDateLabel(entry.date, today);
          const meeting = entry.kind === 'meeting' ? entry.item : null;
          const time = meeting ? new Intl.DateTimeFormat(undefined, {
            timeZone: timezone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
          }).format(new Date(meeting.start_time)) : null;
          return (
            <li className="coming-up__row" key={entry.key}>
              <span className={`coming-up__icon coming-up__icon--${entry.kind}`} aria-hidden="true">
                {meeting ? <CalendarDays size={18} /> : <ListTodo size={18} />}
              </span>
              <div className="coming-up__body">
                <span className="coming-up__kind">{meeting ? 'Meeting' : 'To-do'}</span>
                {meeting && (canViewCohort || (meeting.can_join_video && meeting.is_virtual)) ? (
                  <Link className="coming-up__title" to={canViewCohort
                    ? `/mentorship?${new URLSearchParams({ meetingId: meeting.id, ...(meeting.cohort_id ? { cohortId: meeting.cohort_id } : {}) })}`
                    : `/meetings/${encodeURIComponent(meeting.id)}`}>{meeting.title}</Link>
                ) : entry.kind === 'todo' && hasChecklistLink(entry.item) ? (
                  <button type="button" className="coming-up__title" onClick={() => { void openTask(entry.item); }} disabled={openingId === entry.item.id}>
                    {entry.item.title}{openingId === entry.item.id ? ' · Opening…' : ''}
                  </button>
                ) : <span className="coming-up__title">{entry.item.title}</span>}
                {meeting && <span className="coming-up__location">{time} · {meeting.is_virtual ? 'Online' : meeting.location || 'Location to be confirmed'}</span>}
              </div>
              <span className={`coming-up__date${entry.overdue ? ' coming-up__date--overdue' : ''}`}>{entry.overdue ? `Overdue · ${dateLabel}` : dateLabel}</span>
            </li>
          );
        })}
      </ul>
      <div className="coming-up__status" aria-live="polite">
        {meetingSource.loading && <p>Loading meetings…</p>}
        {taskSource.loading && <p>Loading to-dos…</p>}
        {meetingSource.error && <p>Meetings couldn’t be refreshed. <button type="button" onClick={() => { void meetingSource.refresh(); }}>Retry meetings</button></p>}
        {taskSource.error && <p>To-dos couldn’t be refreshed. <button type="button" onClick={() => { void taskSource.refresh(); }}>Retry to-dos</button></p>}
        {!items.length && !meetingSource.loading && !taskSource.loading && !meetingSource.error && !taskSource.error && <p>No upcoming meetings or dated to-dos.</p>}
        {linkError && <p role="alert">{linkError}</p>}
      </div>
    </section>
  );
};
